import { useState } from "react";
import { api } from "../../api/apiClient";
import type { InvoiceDetail, PaperUse } from "../../api/types";
import {
  paperUseChoices,
  paperUseLabel,
  readerDisagreement,
} from "../../utils/paperUse";
import { PaperUsePicker } from "./PaperUsePicker";

/**
 * G-121 S4 (OD-143, owner 2026-10-02): what this paper is for, why the reader
 * may disagree, and the way to change it — for whoever may upload it (the
 * server holds the three locks: a delivered paper stays a delivery paper, a
 * factura with payments stays a factura).
 */
export function PaperUseBlock({
  invoice,
  onChanged,
}: {
  invoice: InvoiceDetail;
  onChanged: (next: InvoiceDetail) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<PaperUse | null>(invoice.paperUse);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const disagreement = readerDisagreement(invoice.paperUse, invoice.parsedPayload);

  const save = async () => {
    if (!draft || draft === invoice.paperUse) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onChanged(await api.setInvoicePaperUse(invoice.id, draft));
      setEditing(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not change what this paper is");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inv-paper-use">
      <div className="inv-paper-use-head">
        <strong>This paper is</strong>
        <span className="badge primary">{paperUseLabel(invoice.paperUse)}</span>
      </div>
      {disagreement ? <div className="alert warning">{disagreement}</div> : null}
      {error ? <div className="alert error">{error}</div> : null}
      {editing ? (
        <>
          <PaperUsePicker
            name={`paper-use-${invoice.id}`}
            value={draft}
            options={paperUseChoices(invoice.status === "COMMITTED", invoice.paperUse)}
            onChange={setDraft}
            disabled={busy}
            committed={invoice.status === "COMMITTED"}
          />
          <div className="button-row">
            <button type="button" className="button" disabled={busy} onClick={() => void save()}>
              {busy ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              className="button plain"
              disabled={busy}
              onClick={() => {
                setDraft(invoice.paperUse);
                setError(null);
                setEditing(false);
              }}
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <div className="button-row">
          <button type="button" className="button tinted" onClick={() => setEditing(true)}>
            Change what this paper is
          </button>
        </div>
      )}
    </div>
  );
}
